import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Countdown } from "./Countdown";
import { EoiLink } from "./EoiLink";
import { Logo } from "./Logo";

const LINES = ["Where Character", "Meets Academic", "Excellence."];

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14, delayChildren: 0.25 } },
};

const line = {
  hidden: { y: "110%" },
  visible: { y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export const Hero = () => (
  <section className="relative overflow-hidden pt-16 pb-16 lg:pt-24 lg:pb-24">
    <div className="pointer-events-none absolute -top-32 right-0 h-[28rem] w-[28rem] rounded-full bg-gold/10 blur-[120px]" />

    <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
      <div className="flex flex-col justify-center lg:col-span-7">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          className="mb-8 flex items-center gap-4"
        >
          <Logo className="h-14 w-14" />
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">
            VPS · A new charter school · Opening Term 1, 2027
          </p>
        </motion.div>

        <motion.h1
          data-testid="hero-title"
          variants={container}
          initial="hidden"
          animate="visible"
          className="font-serif text-4xl font-bold tracking-tight text-navy sm:text-5xl lg:text-6xl lg:leading-[1.05]"
        >
          {LINES.map((l, i) => (
            <span key={l} className="block overflow-hidden pb-1">
              <motion.span variants={line} className={`block ${i === 1 ? "italic text-gold" : ""}`}>
                {l}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
        >
          VPS pairs disciplined leadership with real-world academics for
          Years 7–10 — and as a charter school there is no school zone, so
          every family is welcome. Expressions of interest are now open for
          the 2027 foundation intake — places are limited.
        </motion.p>

        <motion.p
          data-testid="hero-mission"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="mt-6 max-w-xl border-l-2 border-gold pl-5 font-serif text-lg italic leading-snug text-navy sm:text-xl"
        >
          “Building a generation of leaders who are ready to make a difference.”
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <EoiLink
            testid="hero-eoi-button"
            className="group flex items-center gap-2.5 rounded-full bg-gold px-12 py-6 text-lg font-semibold text-black shadow-xl shadow-gold/30 transition-all duration-200 hover:bg-gold-hover hover:shadow-2xl hover:shadow-gold/40 active:scale-95"
          >
            Express Your Interest
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </EoiLink>
          <a
            href="#ethos"
            data-testid="hero-ethos-button"
            className="rounded-full border border-navy/25 px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-white"
          >
            Discover Our Ethos
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-8 font-mono text-[11px] tracking-[0.2em] text-slate-500"
        >
          YEARS 7–10 · FOUNDATION INTAKE 2027 · LIMITED PLACES
        </motion.p>
      </div>

      <div className="flex flex-col justify-center lg:col-span-5">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8 }}
        >
          <Countdown />
        </motion.div>
      </div>
    </div>
  </section>
);
